import { IEncryptData, valueEncrypt } from "encrypt-value/encrypt";

export const keyToBuffer = (data: string): ArrayBuffer => {
  const buf = new ArrayBuffer(data.length);
  const bufView = new Uint8Array(buf);

  for (let i = 0, strLen = data.length; i < strLen; i++) {
    bufView[i] = data.charCodeAt(i);
  }
  return buf;
};

export const payloadDataToBuffer = (data: string): ArrayBuffer => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  return dataBuffer.buffer;
};

// expects a string with no new lines
export const importPublicKey = async (pub: string): Promise<CryptoKey> => {
  // Assumes no pem header or footer
  const binaryDerString = window.atob(pub);
  const binaryDer = keyToBuffer(binaryDerString);
  const key = await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
  return key;
};

export const base64Encode = (buffer: ArrayBuffer | number[]): string => {
  const uint8Buffer = new Uint8Array(buffer);
  return window.btoa(String.fromCharCode(...uint8Buffer));
};

type EncryptData = {
  cryptoKey: CryptoKey;
  dataBuffer: BufferSource;
};

export const encryptData = async ({
  cryptoKey,
  dataBuffer,
}: EncryptData): Promise<ArrayBuffer> => {
  return window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    cryptoKey,
    dataBuffer
  );
};

export const createEncryptedValueGetter =
  (encryptionKey?: string) =>
  async (value?: string): Promise<IEncryptData> => {
    if (!value || value === "") {
      return { encryptedData: "", encryptionTimestamp: 0, encryptionNonce: "" };
    }
    if (isValueEncrypted(value)) {
      return {
        encryptedData: value,
        encryptionTimestamp: 0,
        encryptionNonce: "",
      };
    }
    if (!encryptionKey) {
      return { encryptedData: "", encryptionTimestamp: 0, encryptionNonce: "" };
    }
    const data = await valueEncrypt({
      value,
      key: encryptionKey,
    });
    return data;
  };

const isBase64Encoded = (str: string): boolean => {
  try {
    // Check if the string length is a multiple of 4
    if (str.length % 4 !== 0) {
      return false;
    }

    // Check if the string contains only valid base64 characters
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
    if (!base64Regex.test(str)) {
      return false;
    }

    // Try to decode the string
    window.atob(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const isValueEncrypted = (value?: string): boolean => {
  if (!value || value == "") {
    return false;
  }
  const splitStrings = value.split(" ");
  return splitStrings.every((s) => s.length > 70 && isBase64Encoded(s));
};
